import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  viewChild,
  type AfterViewInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { NgDialogModule, FromNowAsyncPipe } from '@principle-theorem/ng-shared';
import {
  IReportRun,
  REPORT_RUN_RETENTION_DAYS,
  ReportRunStatus,
} from '@principle-theorem/principle-core/interfaces';
import { ReportRun } from '@principle-theorem/principle-core';
import { FieldValue, Firestore, toTimestamp, type WithRef } from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { type Observable } from 'rxjs';

export interface IReportRunHistoryDialogData {
  runs$: Observable<WithRef<IReportRun>[]>;
  historyBasePath: string[];
}

@Component({
  selector: 'pr-report-run-history-dialog',
  templateUrl: './report-run-history-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTableModule,
    FromNowAsyncPipe,
    RouterModule,
    DatePipe,
  ],
})
export class ReportRunHistoryDialogComponent implements AfterViewInit {
  private _dialogRef = inject(MatDialogRef<ReportRunHistoryDialogComponent>);
  data = inject<IReportRunHistoryDialogData>(MAT_DIALOG_DATA);
  paginator = viewChild(MatPaginator);

  dataSource = new MatTableDataSource<WithRef<IReportRun>>([]);
  displayedColumns = ['date', 'relativeTime', 'tags', 'results', 'actions'];
  ReportRunStatus = ReportRunStatus;

  constructor() {
    this.data.runs$
      .pipe(takeUntilDestroyed())
      .subscribe((runs) => (this.dataSource.data = runs));
  }

  ngAfterViewInit(): void {
    const paginator = this.paginator();
    if (paginator) {
      this.dataSource.paginator = paginator;
    }
  }

  async togglePin(run: WithRef<IReportRun>): Promise<void> {
    const pinned = !run.pinned;
    const newExpiresAt = pinned
      ? undefined
      : toTimestamp(
          moment().add(REPORT_RUN_RETENTION_DAYS, 'days')
        );
    await Firestore.patchDoc(run.ref, {
      pinned,
      expiresAt: newExpiresAt ?? FieldValue.delete(),
    });
  }

  isViewable(run: WithRef<IReportRun>): boolean {
    return ReportRun.isViewable(run);
  }

  isDeletable(run: WithRef<IReportRun>): boolean {
    return (
      run.status === ReportRunStatus.Complete && !run.archivedAt && !run.pinned
    );
  }

  isRestorable(run: WithRef<IReportRun>): boolean {
    return ReportRun.isRestorable(run);
  }

  isExpiringSoon(run: WithRef<IReportRun>): boolean {
    if (!run.expiresAt) {
      return false;
    }
    return run.expiresAt.toMillis() <= toTimestamp().toMillis();
  }

  async archiveRun(run: WithRef<IReportRun>): Promise<void> {
    await Firestore.patchDoc(run.ref, {
      archivedAt: toTimestamp(),
      pinned: false,
      expiresAt: FieldValue.delete(),
    });
  }

  async unarchiveRun(run: WithRef<IReportRun>): Promise<void> {
    const expiresAt = toTimestamp(
      moment().add(REPORT_RUN_RETENTION_DAYS, 'days')
    );
    await Firestore.patchDoc(run.ref, {
      archivedAt: FieldValue.delete(),
      expiresAt,
    });
  }

  navigateToRun(): void {
    this._dialogRef.close();
  }
}
